import { NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { CartComponent } from 'app/layout/common/cart/cart.component';
import { FooterComponent } from 'app/layout/common/footer/footer.component';
import { GenericModalComponent } from 'app/layout/common/generic-modal/generic-modal.component';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { TFMCartService } from 'app/mock-api/tfm-cart.service';
import { TFMService } from 'app/mock-api/tfm.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'dense-layout',
    templateUrl: './dense.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [FuseLoadingBarComponent, FuseVerticalNavigationComponent, MatButtonModule, MatIconModule, MatSidenavModule, LanguagesComponent, FuseFullscreenComponent, SearchComponent, ShortcutsComponent, MessagesComponent, NotificationsComponent, UserComponent, NgIf, RouterOutlet, QuickChatComponent, FooterComponent, CartComponent],
})
export class DenseLayoutComponent implements OnInit, OnDestroy {
    latestChangelog: any;
    isScreenSmall: boolean;
    navigation: Navigation;
    navigationAppearance: 'default' | 'dense' = 'dense';
    public drawerMode: 'over' | 'side' | 'push' = 'push';

    public view: any = {}

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _tfmService: TFMService,
        private _tfmCartService: TFMCartService,
        public dialog: MatDialog,
        private _cd: ChangeDetectorRef
    ) {
    }

    /** Whether the current route is the search result screen (cart should show limited columns). */
    isSearchResultRoute(): boolean {
        return this._router.url.includes('search-results');
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.view.isClient = this._tfmService.view.isClient;
        this.view.data = this.view.data || { cart: [] };
        this.view.data.cart = this._tfmCartService.getCart();

        this._tfmService.getSite().then(res => {
            this.view.site = res;
        });

        // Keep cart drawer in sync with TFMCartService so "+ New" and clearCart update the drawer
        this.view.cartChange$ = this._tfmCartService.cartChange
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(res => {
                this.view.data.cart = res;
                this._cd.detectChanges();
            });

        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');

                // Change the navigation appearance
                this.navigationAppearance = this.isScreenSmall ? 'default' : 'dense';
            });

        // this.view.itinGuidChange$ = this._tfmService.itinGuidChange.subscribe(res => {
        //     this.getItin();
        // });

        // this.getItin();

        this.view.itinGuidChange$ = this._tfmService.itinGuidChange.subscribe(res => {
            this.view.itinGuid = this._tfmService.getLocalItinGuid();

            //this._changeDetectorRef.detectChanges();
        });

        this.view.itinGuid = this._tfmService.getLocalItinGuid();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    /**
     * Toggle the navigation appearance
     */
    toggleNavigationAppearance(): void {
        this.navigationAppearance = (this.navigationAppearance === 'default' ? 'dense' : 'default');
    }

    openSearchOptions() {
        this._tfmCartService.clearCart();
        this._tfmService.clearLocalItinGuid();
        this.dialog.open(GenericModalComponent, {
            data: { type: 'search-options' },
            height: '90vh',
            width: '90vw',
            maxWidth: '90vw'
        });
    }

    viewChanged(options: any): void {
        this.view.drawerOptions = options;
    }

    quoteCreated(quoteGuid: string): void {
        if (quoteGuid) {
            this._router.navigate(['/checkout', quoteGuid]);
        }
    }

    getItin() {
        this.view.itin = null;
        var itinGuid = this._tfmService.getLocalItinGuid();
        // console.log('testing.....',itinGuid)
        if (itinGuid) {
            this._tfmService.getItin(itinGuid).subscribe(res => {
                this.view.itin = res;
                this._tfmService.log("Get Iten...", this.view.itin);
                this._cd.detectChanges();
            });
        }
        // console.log('decline')
    }
}
